import React, { useState, useEffect, useRef } from 'react';
import { BroadcastParams, ScheduledBroadcast } from '../types';
import api, { broadcastAPI, groupsAPI, newsAPI, NewsListItem } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import Header from './Header';
import './News.css';

const News: React.FC = () => {
  const [text, setText] = useState('');
  const [sendToBot, setSendToBot] = useState(true);
  const [sendToApp, setSendToApp] = useState(true);

  const [broadcastParams, setBroadcastParams] = useState<BroadcastParams>({
    html: '',
    onlyActive: false,
    allUsers: false,
    whitelistMode: false,
    includeNewUsers: true,
    includeTeachers: true,
    includeHasApp: true,
    group: 'all',
    scheduleTime: '',
  });

  const [preview, setPreview] = useState('');
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [isLoadingScheduled, setIsLoadingScheduled] = useState(false);
  const [groups, setGroups] = useState<string[]>(['all']);
  const [scheduledBroadcasts, setScheduledBroadcasts] = useState<ScheduledBroadcast[]>([]);
  const [showScheduledModal, setShowScheduledModal] = useState(false);

  const [photos, setPhotos] = useState<Array<{ previewUrl: string; uploadedUrl: string }>>([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sentNewsList, setSentNewsList] = useState<NewsListItem[]>([]);
  const [isLoadingSentNews, setIsLoadingSentNews] = useState(false);
  const [sentNewsPage, setSentNewsPage] = useState(0);
  const [sentNewsTotal, setSentNewsTotal] = useState(0);
  const [deletingNewsId, setDeletingNewsId] = useState<string | null>(null);
  const [isSendingTest, setIsSendingTest] = useState(false);
  type ChannelFilter = 'all' | 'app_only' | 'bot_only' | 'both';
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');

  const MAX_PHOTOS = 3;
  const SENT_NEWS_LIMIT = 20;

  useEffect(() => {
    loadGroups();
  }, []);

  const loadSentNews = async (page: number = 0, append: boolean = false) => {
    setIsLoadingSentNews(true);
    try {
      const result = await newsAPI.getNewsList(page, SENT_NEWS_LIMIT);
      setSentNewsList(prev => append ? [...prev, ...result.news] : result.news);
      setSentNewsTotal(result.total);
      setSentNewsPage(result.page);
    } catch (error) {
      console.error('Failed to load sent news:', error);
    } finally {
      setIsLoadingSentNews(false);
    }
  };

  useEffect(() => {
    loadSentNews(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBroadcastParams(prev => ({ ...prev, html: text }));
  }, [text]);

  const loadGroups = async () => {
    try {
      const result = await groupsAPI.getGroups();
      if (result.success) {
        setGroups(['all', ...result.groups]);
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  const handlePreview = () => {
    setPreview(text || '<i style="color:#7f8c8d;">Пусто</i>');
  };

  const handleCount = async () => {
    setIsLoadingCount(true);
    try {
      const result = await broadcastAPI.getCount({ ...broadcastParams, html: text });
      if (result.success) {
        setRecipientCount(result.count);
      }
    } catch (error) {
      console.error('Failed to get count:', error);
    } finally {
      setIsLoadingCount(false);
    }
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      alert(`Максимум ${MAX_PHOTOS} фото в одном сообщении`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Выберите изображение (jpeg, png, webp)');
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setIsUploadingPhoto(true);
    try {
      const result = await newsAPI.uploadPhoto(file);
      if (result.success && result.url) {
        const baseUrl = (api.defaults.baseURL || '').replace(/\/$/, '');
        const uploadedUrl = `${baseUrl}${result.url}`;
        setPhotos(prev => [...prev, { previewUrl, uploadedUrl }]);
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Ошибка загрузки фото');
      URL.revokeObjectURL(previewUrl);
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      alert('Введите текст новости');
      return;
    }
    if (!sendToBot && !sendToApp) {
      alert('Выберите хотя бы один канал');
      return;
    }

    if (sendToBot) {
      const count = recipientCount ?? 0;
      if (!window.confirm(`Сообщение будет отправлено в бот ${count} пользователям. Вы уверены?`)) {
        return;
      }
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * 10) + 1;
      const answer = prompt(`Сколько будет ${a} + ${b}?`);
      if (parseInt(answer || '') !== a + b) {
        alert('Неверно!');
        return;
      }
    }

    if (sendToApp && !sendToBot) {
      if (!window.confirm('Отправить новость в приложение?')) return;
    }

    if (sendToBot && sendToApp) {
      if (!window.confirm('Отправить в бот и приложение?')) return;
    }

    const imageUrls = photos.map(p => p.uploadedUrl);
    const scheduleTime = broadcastParams.scheduleTime ? broadcastParams.scheduleTime.trim() : '';
    const scheduleDate = scheduleTime ? new Date(scheduleTime) : null;
    const isScheduled = scheduleDate != null && scheduleDate > new Date();

    if (isScheduled) {
      const channels = [];
      if (sendToBot) channels.push('бот');
      if (sendToApp) channels.push('приложение');
      if (!window.confirm(`Запланировать отправку в ${channels.join(' и ')} на ${scheduleDate.toLocaleString('ru-RU')}?`)) return;
      if (sendToBot) {
        const a = Math.floor(Math.random() * 10) + 1;
        const b = Math.floor(Math.random() * 10) + 1;
        const answer = prompt(`Сколько будет ${a} + ${b}?`);
        if (parseInt(answer || '') !== a + b) {
          alert('Неверно!');
          return;
        }
      }
      setIsLoading(true);
      try {
        const result = await newsAPI.scheduleNews({
          message: trimmed,
          imageUrls,
          sendToBot,
          sendToApp,
          scheduleTime: scheduleDate.toISOString(),
          onlyActive: broadcastParams.onlyActive,
          allUsers: broadcastParams.allUsers,
          whitelistMode: broadcastParams.whitelistMode,
          includeNewUsers: broadcastParams.includeNewUsers,
          includeTeachers: broadcastParams.includeTeachers,
          includeHasApp: broadcastParams.includeHasApp,
          group: broadcastParams.group || 'all',
        });
        if (result.success && result.scheduled) {
          alert(`Новость запланирована на ${result.scheduledFor ? new Date(result.scheduledFor).toLocaleString('ru-RU') : ''}`);
          setBroadcastParams(prev => ({ ...prev, scheduleTime: '' }));
        } else {
          alert('Ошибка планирования: ' + (result.error || 'unknown'));
        }
      } catch (error) {
        console.error('Failed to schedule:', error);
        alert('Ошибка при планировании');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    const errors: string[] = [];

    try {
      let botSentRefs: { chatId: number; messageId: number }[] | undefined;
      if (sendToBot) {
        const params = { ...broadcastParams, html: trimmed, imageUrls, scheduleTime: '' };
        const result = await broadcastAPI.send(params);
        if (result.success) {
          botSentRefs = result.sentRefs;
          if (result.scheduled) {
            alert(`Рассылка в бот запланирована на ${result.scheduledFor ? new Date(result.scheduledFor).toLocaleString('ru-RU') : ''}`);
          } else {
            alert(`Бот: отправлено ${result.sent} из ${result.total}, ошибок: ${result.failed}`);
          }
          if (!sendToApp) {
            await newsAPI.logNews({ message: trimmed, imageUrls, botSentRefs });
            loadSentNews(0);
          }
        } else {
          errors.push('Бот: ' + (result.error || 'unknown'));
        }
      }

      if (sendToApp) {
        const result = await newsAPI.sendNews({ message: trimmed, imageUrls, sentToBot: sendToBot, botSentRefs });
        if (result.success) {
          alert(`Приложение: отправлено ${result.sentCount} пользователям, ошибок: ${result.errorCount || 0}`);
          loadSentNews(0);
        } else {
          errors.push('Приложение: ошибка отправки');
        }
      }

      if (errors.length > 0) {
        alert('Ошибки: ' + errors.join('; '));
      }
    } catch (error) {
      console.error('Failed to send:', error);
      alert('Ошибка при отправке');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowScheduled = async () => {
    setShowScheduledModal(true);
    setIsLoadingScheduled(true);
    try {
      const result = await broadcastAPI.getScheduled();
      if (result.success) {
        setScheduledBroadcasts(result.scheduled);
      }
    } catch (error) {
      console.error('Failed to load scheduled broadcasts:', error);
    } finally {
      setIsLoadingScheduled(false);
    }
  };

  const handleDeleteScheduled = async (id: string) => {
    if (!window.confirm('Удалить эту рассылку?')) return;
    try {
      const result = await broadcastAPI.deleteScheduled(id);
      if (result.success) {
        setScheduledBroadcasts(prev => prev.filter(b => b.id !== id));
      } else {
        alert('Ошибка удаления: ' + (result.error || 'unknown'));
      }
    } catch (error) {
      console.error('Failed to delete scheduled broadcast:', error);
      alert('Ошибка сети при удалении');
    }
  };

  const updateParam = (key: keyof BroadcastParams, value: any) => {
    setBroadcastParams(prev => ({ ...prev, [key]: value }));
  };

  const handleDeleteNews = async (id: string) => {
    if (!window.confirm('Удалить эту новость из списка?')) return;
    setDeletingNewsId(id);
    try {
      await newsAPI.deleteNews(id);
      setSentNewsList(prev => prev.filter(n => n.id !== id));
      setSentNewsTotal(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to delete news:', error);
      alert('Не удалось удалить новость');
    } finally {
      setDeletingNewsId(null);
    }
  };

  const handleSendTest = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      alert('Введите текст новости');
      return;
    }
    if (!window.confirm('Отправить тестовую новость пользователю 1297952533?')) return;
    setIsSendingTest(true);
    try {
      const imageUrls = photos.map(p => p.uploadedUrl);
      const result = await newsAPI.sendTest({ message: trimmed, imageUrls });
      if (result.success) {
        alert('Тестовая новость отправлена');
      } else {
        alert('Ошибка: ' + (result.error || 'не удалось отправить'));
      }
    } catch (error) {
      console.error('Failed to send test news:', error);
      alert('Ошибка при тестовой отправке');
    } finally {
      setIsSendingTest(false);
    }
  };

  const getNewsImageUrl = (item: NewsListItem): string | null => {
    const url = item.imageUrls?.[0] || item.imageUrl;
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const baseUrl = (api.defaults.baseURL || '').replace(/\/$/, '');
    return `${baseUrl}${url}`;
  };

  const getChannelBadge = (item: NewsListItem): { emoji: string; title: string } => {
    const toBot = item.sentToBot === true;
    const toApp = item.sentToApp !== false;
    if (toBot && toApp) return { emoji: '📱📲', title: 'В бот и в приложение' };
    if (toBot) return { emoji: '📱', title: 'Только в бот' };
    return { emoji: '📲', title: 'Только в приложение' };
  };

  const matchesChannelFilter = (item: NewsListItem): boolean => {
    const toBot = item.sentToBot === true;
    const toApp = item.sentToApp !== false;
    switch (channelFilter) {
      case 'app_only': return !toBot && toApp;
      case 'bot_only': return toBot && !toApp;
      case 'both': return toBot && toApp;
      default: return true;
    }
  };

  const filteredSentNews = sentNewsList.filter(matchesChannelFilter);

  return (
    <div className="news-page">
      <Header title="Новости" />
      <main className="main-content">
        <div className="page-container">
          <div className="news-header">
            <h2>Отправка новости</h2>
            <p>Отправка новостей в бот Telegram и/или в мобильное приложение</p>
          </div>
          <div className="news-content">
            <div className="news-form">

              <div className="news-targets">
                <span className="news-targets-label">Куда отправить:</span>
                <div className="news-target-cards">
                  <div
                    className={`news-target-card ${sendToBot ? 'selected' : ''}`}
                    onClick={() => setSendToBot(prev => (prev && !sendToApp) ? true : !prev)}
                  >
                    <span className="news-target-icon">📱</span>
                    <span>В бот Telegram</span>
                  </div>
                  <div
                    className={`news-target-card ${sendToApp ? 'selected' : ''}`}
                    onClick={() => setSendToApp(prev => (prev && !sendToBot) ? true : !prev)}
                  >
                    <span className="news-target-icon">📲</span>
                    <span>В приложение</span>
                  </div>
                </div>
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={sendToBot ? "Введите HTML-сообщение (поддерживаются теги Telegram)" : "Введите текст новости"}
                rows={6}
              />

              {sendToBot && (
                <div className="news-bot-section">
                  <div className="broadcast-controls">
                    <div className="control-row">
                      <label>
                        <input type="checkbox" checked={broadcastParams.onlyActive} onChange={(e) => updateParam('onlyActive', e.target.checked)} />
                        Только активным
                      </label>
                    </div>
                    <div className="control-row">
                      <label>
                        <input type="checkbox" checked={broadcastParams.allUsers} onChange={(e) => updateParam('allUsers', e.target.checked)} />
                        Все пользователи
                      </label>
                      <label>
                        <input type="checkbox" checked={broadcastParams.whitelistMode} onChange={(e) => updateParam('whitelistMode', e.target.checked)} />
                        Только отмеченные категории (whitelist)
                      </label>
                    </div>
                    <div className="control-row">
                      <label>
                        <input type="checkbox" checked={broadcastParams.includeNewUsers} onChange={(e) => updateParam('includeNewUsers', e.target.checked)} disabled={broadcastParams.allUsers} />
                        Включать новых пользователей
                      </label>
                      <label>
                        <input type="checkbox" checked={broadcastParams.includeTeachers} onChange={(e) => updateParam('includeTeachers', e.target.checked)} disabled={broadcastParams.allUsers} />
                        Включать учителей
                      </label>
                      <label>
                        <input type="checkbox" checked={broadcastParams.includeHasApp} onChange={(e) => updateParam('includeHasApp', e.target.checked)} disabled={broadcastParams.allUsers} />
                        Включать пользователей с приложением (FCM)
                      </label>
                    </div>
                    <div className="control-row">
                      <label>
                        Группа:
                        <select value={broadcastParams.group} onChange={(e) => updateParam('group', e.target.value)}>
                          {groups.map(g => <option key={g} value={g}>{g === 'all' ? 'Все группы' : g}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>
                  <div className="broadcast-actions">
                    <button onClick={handlePreview}>Предпросмотр</button>
                    <button onClick={handleCount} disabled={isLoadingCount}>
                      {isLoadingCount ? <><span className="inline-spinner"></span> Подсчет...</> : 'Узнать количество получателей'}
                    </button>
                    <button onClick={handleShowScheduled} className="scheduled-btn">Запланированные</button>
                  </div>
                  <div className="broadcast-info">
                    <label>
                      <span>Запланировать на:</span>
                      <input type="datetime-local" value={broadcastParams.scheduleTime} onChange={(e) => updateParam('scheduleTime', e.target.value)} />
                    </label>
                    {recipientCount !== null && <span className="count-info">Получателей: {recipientCount}</span>}
                  </div>
                  {preview && (
                    <div className="preview-box">
                      <div dangerouslySetInnerHTML={{ __html: preview }} />
                    </div>
                  )}
                </div>
              )}

              {sendToApp && !sendToBot && (
                <div className="news-bot-section">
                  <div className="broadcast-actions">
                    <button onClick={handleShowScheduled} className="scheduled-btn">Запланированные</button>
                  </div>
                  <div className="broadcast-info">
                    <label>
                      <span>Запланировать на:</span>
                      <input type="datetime-local" value={broadcastParams.scheduleTime} onChange={(e) => updateParam('scheduleTime', e.target.value)} />
                    </label>
                  </div>
                </div>
              )}

              {(sendToBot || sendToApp) && (
                <div className="news-photo-section">
                  <div className="news-photo-upload">
                    <label className="news-photo-label">Фото для новости (опционально, макс. {MAX_PHOTOS}):</label>
                    {photos.length < MAX_PHOTOS && (
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoSelect} className="news-photo-input" disabled={isUploadingPhoto} />
                    )}
                    {photos.length > 0 && (
                      <div className="news-photo-list">
                        {photos.map((p, i) => (
                          <div key={i} className="news-photo-preview">
                            <img src={p.previewUrl} alt={`Превью ${i + 1}`} />
                            <button type="button" onClick={() => handleRemovePhoto(i)} className="news-photo-remove">Удалить</button>
                          </div>
                        ))}
                        {isUploadingPhoto && <span className="news-photo-status">Загрузка...</span>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="news-send-row">
                <button onClick={handleSend} disabled={isLoading} className="send-btn">
                  {isLoading ? <><span className="inline-spinner"></span> Отправка...</> : 'Отправить'}
                </button>
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={isSendingTest || isLoading || !text.trim()}
                  className="send-test-btn"
                  title="Отправить текущий текст и фото тестово пользователю 1297952533"
                >
                  {isSendingTest ? <><span className="inline-spinner"></span> Отправка...</> : 'Отправить тестовое'}
                </button>
              </div>
            </div>

            <div className="sent-news-section">
              <h3 className="sent-news-title">Отправленные новости</h3>
              <div className="sent-news-actions">
                <label className="sent-news-filter-label">
                  Канал:
                  <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value as ChannelFilter)} className="sent-news-filter-select">
                    <option value="all">Все</option>
                    <option value="both">📱📲 Бот и приложение</option>
                    <option value="app_only">📲 Только приложение</option>
                    <option value="bot_only">📱 Только бот</option>
                  </select>
                </label>
                <button type="button" onClick={() => loadSentNews(0)} disabled={isLoadingSentNews} className="sent-news-refresh-btn">
                  {isLoadingSentNews ? <><span className="inline-spinner"></span> Загрузка...</> : 'Обновить'}
                </button>
              </div>
              {isLoadingSentNews && sentNewsList.length === 0 ? (
                <div className="sent-news-loading">
                  <LoadingSpinner text="Загрузка списка новостей..." />
                </div>
              ) : sentNewsList.length === 0 ? (
                <div className="sent-news-empty">Нет отправленных новостей</div>
              ) : filteredSentNews.length === 0 ? (
                <div className="sent-news-empty">Нет новостей по выбранному фильтру</div>
              ) : (
                <div className="sent-news-list">
                  {filteredSentNews.map((item) => {
                    const badge = getChannelBadge(item);
                    return (
                      <div key={item.id} className="sent-news-card">
                        <div className="sent-news-card-header">
                          <span className="sent-news-channel-badge" title={badge.title}>{badge.emoji}</span>
                          <span className="sent-news-date">
                            {new Date(item.timestamp).toLocaleString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          <button
                            type="button"
                            className="sent-news-delete-btn"
                            title="Удалить"
                            onClick={() => handleDeleteNews(item.id)}
                            disabled={deletingNewsId === item.id}
                          >
                            {deletingNewsId === item.id ? <LoadingSpinner /> : '🗑 Удалить'}
                          </button>
                        </div>
                        {getNewsImageUrl(item) && (
                          <div className="sent-news-preview-img">
                            <img src={getNewsImageUrl(item)!} alt="" />
                          </div>
                        )}
                        <div className="sent-news-content">
                          {(item.content.replace(/<[^>]+>/g, '').trim().substring(0, 200) || item.content.substring(0, 200))}
                          {item.content.length > 200 ? '...' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {sentNewsTotal > SENT_NEWS_LIMIT && (
                <div className="sent-news-pagination">
                  Показано {filteredSentNews.length} из {channelFilter === 'all' ? sentNewsTotal : filteredSentNews.length}
                  {sentNewsList.length < sentNewsTotal && channelFilter === 'all' && (
                    <button type="button" onClick={() => loadSentNews(sentNewsPage + 1, true)} disabled={isLoadingSentNews} className="sent-news-more-btn">
                      Ещё
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {showScheduledModal && (
        <div className="modal-overlay" onClick={() => setShowScheduledModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Запланированные рассылки</h3>
              <button onClick={() => setShowScheduledModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {isLoadingScheduled ? (
                <LoadingSpinner text="Загрузка запланированных рассылок..." />
              ) : scheduledBroadcasts.length === 0 ? (
                <div className="no-scheduled">Нет запланированных рассылок</div>
              ) : (
                <div className="scheduled-list">
                  {scheduledBroadcasts.map(b => {
                    const toBot = b.sendToBot !== false;
                    const toApp = b.sendToApp === true;
                    const channelLabel = toBot && toApp ? '📱📲 В бот и приложение' : toApp ? '📲 В приложение' : '📱 В бот';
                    return (
                      <div key={b.id} className="scheduled-item">
                        <div className="scheduled-time">Время: <strong>{new Date(b.scheduleTime).toLocaleString('ru-RU')}</strong></div>
                        <div className="scheduled-channel">{channelLabel}</div>
                        <div className="scheduled-text">Текст: {b.html.substring(0, 200)}{b.html.length > 200 && '...'}</div>
                        {toBot && <div className="scheduled-settings">Настройки: {JSON.stringify({ onlyActive: b.onlyActive, allUsers: b.allUsers, group: b.group }, null, 2)}</div>}
                        <div className="scheduled-status">Статус: {b.status}{b.sentAt && `, отправлено: ${new Date(b.sentAt).toLocaleString('ru-RU')}`}</div>
                        {b.status === 'scheduled' && (
                          <button onClick={() => handleDeleteScheduled(b.id)} className="delete-scheduled-btn">Удалить</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
